import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function PayPalCheckoutForm({ paymentMethod = 'paypal', amount, remainingAmount }) {
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const displayAmount = amount || 0;

  const getPaymentMethodTitle = () => {
    switch(paymentMethod) {
      case 'cod': return 'Cash on Delivery - Advance Payment';
      case 'upi': return 'UPI Payment';
      case 'paypal': 
      default: return 'PayPal Payment';
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(`Processing ${paymentMethod.toUpperCase()} payment...`);

    // Mock payment processing
    setTimeout(() => {
      setIsLoading(false);
      setMessage("Payment successful! Redirecting...");
      
      // Generate payment ID based on method
      const transactionId = `${paymentMethod.toUpperCase()}_${Date.now()}_${Math.random().toString(36).substring(2, 11).toUpperCase()}`;
      localStorage.setItem("paymentID", transactionId);
      localStorage.setItem("paymentMethod", paymentMethod);
      if (paymentMethod === 'cod') {
        localStorage.setItem("codAdvance", displayAmount.toString());
        localStorage.setItem("codRemaining", remainingAmount?.toString() || '0');
      }
      
      // Redirect to payment success page
      setTimeout(() => {
        navigate('/payment');
      }, 1000);
    }, 2000);
  };

  return (
    <div className="w-full">
      {/* Payment Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{getPaymentMethodTitle()}</h3>
          {paymentMethod === 'paypal' && (
            <svg className="h-8" viewBox="0 0 124 33" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746z" fill="#253B80"/>
              <path d="M47 13.154c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906z" fill="#253B80"/>
            </svg>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 mb-1">Test Mode Active</p>
                <p className="text-sm text-blue-700">
                  {paymentMethod === 'cod' 
                    ? `Pay advance of ₹${displayAmount.toFixed(2)} now. Remaining ₹${remainingAmount?.toFixed(2) || '0'} will be collected on delivery.`
                    : `This is a demo payment. Click the button below to simulate a ${paymentMethod.toUpperCase()} payment.`
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Amount to Pay:</span>
                <span className="text-2xl font-bold text-gray-900">₹{displayAmount.toFixed(2)}</span>
              </div>
              {paymentMethod === 'cod' && remainingAmount > 0 && (
                <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Pay on Delivery:</span>
                  <span className="font-semibold text-amber-700">₹{remainingAmount.toFixed(2)}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              {paymentMethod === 'paypal' && 'Secure payment powered by PayPal'}
              {paymentMethod === 'cod' && 'Advance payment required for Cash on Delivery'}
              {paymentMethod === 'upi' && 'Instant payment via UPI'}
            </p>
          </div>

          <button
            onClick={handlePayment}
            disabled={isLoading}
            type="button"
            style={{ 
              minHeight: '56px',
              fontSize: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '16px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? '0.5' : '1',
              transition: 'all 0.2s',
              backgroundColor: paymentMethod === 'cod' ? '#d97706' : paymentMethod === 'upi' ? '#9333ea' : '#0070ba',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = paymentMethod === 'cod' ? '#b45309' : paymentMethod === 'upi' ? '#7e22ce' : '#005ea6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = paymentMethod === 'cod' ? '#d97706' : paymentMethod === 'upi' ? '#9333ea' : '#0070ba';
              }
            }}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                {paymentMethod === 'paypal' && (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 00-.794.68l-.04.22-.63 3.993-.028.15a.428.428 0 01-.424.36h-2.95a.625.625 0 01-.617-.73l1.692-10.722a1.125 1.125 0 011.11-.955h2.788c1.245 0 2.318-.045 3.195-.24.842-.187 1.563-.49 2.14-.903.316-.226.592-.486.822-.782z"/>
                  </svg>
                )}
                <span>
                  {paymentMethod === 'cod' && `Pay Advance ₹${displayAmount.toFixed(2)}`}
                  {paymentMethod === 'paypal' && 'Pay with PayPal'}
                  {paymentMethod === 'upi' && 'Pay with UPI'}
                </span>
              </>
            )}
          </button>

          {message && (
            <div className={`p-3 rounded-lg text-center text-sm ${
              message.includes('successful') 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`}>
              {message}
            </div>
          )}
        </div>
      </div>

      {/* Security Message */}
      <div className="text-center text-sm text-gray-500 mt-4">
        <p>Secure payment processing</p>
        <p className="mt-1">Your payment information is encrypted and secure</p>
      </div>
    </div>
  );
}
